import { Dialog, DialogTitle, DialogContent, Box, IconButton, Divider, useTheme } from "@mui/material";
import Close from "@mui/icons-material/Close";
import { FoodsIcon, FoodsIconButton, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { ValidationMessage } from "src/components/QuestionnaireForm/FormProperty/validationTypes";
import { resolveUrlForProperty } from "src/utils/useQuestionnaireNavigation.ts";
import { Link, useParams } from "react-router";
import { ActionPlan } from "src/api/client-api/ActionPlan/ActionPlan";

type ActionViolationErrorsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    validationMessages: ValidationMessage[];
    actionPlan: ActionPlan;
    scoreId: number;
};

export const ActionViolationErrorsModal = (props: ActionViolationErrorsModalProps) => {
    const { isOpen, onClose, actionPlan, validationMessages, scoreId } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const params = useParams();

    const errorMessages = validationMessages.filter((msg) => msg.severity === "error");

    return (
        <Dialog maxWidth="sm" fullWidth open={isOpen} onClose={onClose}>
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: "16px" }}>
                        {t("validation.errors.modal_title", "Errors & warnings")}
                    </Typography>
                    <IconButton onClick={onClose} edge="end" size="small" aria-label="close" sx={{ p: 1 }}>
                        <Close sx={{ fontSize: 28 }} />
                    </IconButton>
                </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 3 }}>
                <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                                backgroundColor: theme.custom.red[50],
                                borderRadius: "4px",
                                px: 1,
                                py: 0.25,
                                height: "26px",
                                width: "fit-content",
                            }}
                        >
                            <Box sx={{ color: theme.custom.red[600], display: "flex", alignItems: "center" }}>
                                <FoodsIcon icon="cancel" size={1.5} color="inherit" />
                            </Box>
                            <Typography
                                variant="body-medium"
                                sx={{ fontWeight: 700, fontSize: 14, color: theme.custom.red[600] }}
                            >
                                {t("validation.errors.error_badge", "Errors")}
                            </Typography>
                        </Box>
                        <Typography variant="body-medium" sx={{ fontWeight: 700, fontSize: 14 }}>
                            {t(
                                "action_plan.violation_errors_description",
                                "The action could not be added due to the following errors",
                            )}
                            :
                        </Typography>
                    </Box>

                    {errorMessages.map((validationMessage, index) => {
                        const propertyName = validationMessage.hint?.propertyName;
                        const questionnaireLink = resolveUrlForProperty(
                            {
                                ...params,
                                campaignId: actionPlan.startSsqPost.campaign?.id,
                                scoreId,
                            },
                            validationMessage,
                            location.pathname,
                        );

                        return (
                            <Box
                                key={`error-${index}`}
                                display="flex"
                                alignItems="center"
                                sx={{
                                    borderLeft: `2px solid ${theme.custom.red[600]}`,
                                    pl: 2,
                                }}
                            >
                                <Box flex={1} display="flex" flexDirection="column">
                                    {propertyName && (
                                        <Typography variant="body-medium" sx={{ fontWeight: 600, fontSize: 14 }}>
                                            {propertyName}
                                        </Typography>
                                    )}
                                    <Typography variant="body-medium" sx={{ fontWeight: 300, fontSize: 14 }}>
                                        {validationMessage.message}
                                    </Typography>
                                </Box>
                                {questionnaireLink !== null && (
                                    <Link to={questionnaireLink}>
                                        <FoodsIconButton
                                            size={"xs"}
                                            icon={"openNewTab"}
                                            tooltip={{
                                                text: t(
                                                    "validation.errors.correct_tooltip",
                                                    "Click to correct this in the questionnaire.",
                                                ),
                                            }}
                                        />
                                    </Link>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </DialogContent>
        </Dialog>
    );
};
